import React, { useEffect, useState } from "react";
import { useKT } from "../i18n/kt";

/**
 * "Install the Karkhana app": Android Chrome's install prompt when it offers one, otherwise a one-line
 * hint for Safari or Chrome's menu. Hidden once the page already runs as the installed app.
 */

let deferred: any = null;

/** True while the page links the Karkhana manifest (supervisor pages). */
export const isKarkhanaManifest = (): boolean =>
  (document.querySelector('link[rel="manifest"]')?.getAttribute("href") || "").includes("karkhana-manifest");
const listeners = new Set<() => void>();
if (typeof window !== "undefined") {
  // Chrome fires this once, early; keep it until a page with the button is open. Only a prompt fired
  // while the Karkhana manifest was linked installs Karkhana; one for the main HKP app is ignored.
  window.addEventListener("beforeinstallprompt", (e) => {
    if (!isKarkhanaManifest()) {
      deferred = null; // the main HKP app's prompt: leave Chrome's own behaviour alone
      return;
    }
    e.preventDefault();
    deferred = e;
    listeners.forEach((l) => l());
  });
}

const standalone = (): boolean =>
  typeof window !== "undefined" &&
  ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || (navigator as any).standalone === true);

const InstallKarkhanaApp = () => {
  const { t } = useKT();
  const [canPrompt, setCanPrompt] = useState(!!deferred);

  useEffect(() => {
    const l = () => setCanPrompt(!!deferred);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  if (standalone()) return null;

  if (canPrompt) {
    return (
      <button
        type="button"
        className="ksl__install"
        onClick={async () => {
          const e = deferred;
          deferred = null;
          setCanPrompt(false);
          await e?.prompt?.();
        }}
      >
        {t("Install the Karkhana app")}
      </button>
    );
  }

  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  return (
    <p className="ksl__install-hint">
      <strong>{t("Put Karkhana on your home screen")}</strong>
      <br />
      {ios ? t("In Safari tap Share, then “Add to Home Screen”.") : t("In Chrome tap ⋮, then “Add to Home screen”.")}
    </p>
  );
};

export default InstallKarkhanaApp;
